import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { CheckCircle2, ArrowRight, ListChecks, Boxes, Sprout } from "lucide-react";

const pick = (lang, hi, en) => (lang === "हिंदी" ? hi : en);

export default function TodayMode() {
  const { user } = useAuth();
  const { language } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/data/activities").then((r) => r.data).catch(() => []),
      api.get("/data/crop-cycles").then((r) => r.data).catch(() => []),
      api.get("/data/inventory").then((r) => r.data).catch(() => []),
    ]).then(([acts, cycles, inv]) => setData({ acts, cycles, inv }));
  }, []);

  if (user?.role !== "farmer" || !data) return null;
  const mode = user?.mode || "beginner";
  const planned = (data.acts || []).filter((a) => a.status === "Planned")
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const activeCycles = (data.cycles || []).filter((c) => c.status === "Active");
  const lowStock = (data.inv || []).filter((it) => (it.quantity ?? 0) <= (it.reorderLevel ?? 0));

  if (mode === "pro") {
    // dense, exceptions-first strip
    const Cell = ({ icon: I, label, value, to }) => (
      <button onClick={() => navigate(to)} className="flex-1 min-w-[140px] text-left rounded-xl bg-white/80 border p-3 hover:shadow-sm">
        <I className="h-4 w-4 text-[#1B7A3E]" /><p className="text-2xl font-extrabold tabular-nums mt-1">{value}</p>
        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
      </button>
    );
    return (
      <div className="flex flex-wrap gap-3 mb-5" data-testid="today-pro">
        <Cell icon={Sprout} label={pick(language, "सक्रिय फसलें", "Active crops")} value={activeCycles.length} to="/operations" />
        <Cell icon={ListChecks} label={pick(language, "बाकी काम", "Pending tasks")} value={planned.length} to="/operations" />
        <Cell icon={Boxes} label={pick(language, "कम स्टॉक", "Low stock")} value={lowStock.length} to="/inventory" />
      </div>
    );
  }

  // beginner: one guided task at a time
  const next = planned[0];
  return (
    <div className="rounded-[22px] bg-[#1B7A3E] text-white p-5 mb-5" data-testid="today-beginner">
      <p className="text-[11px] font-bold uppercase tracking-widest text-white/70 mb-1">{pick(language, "आज का काम", "Today's one task")}</p>
      {next ? (
        <>
          <p className="text-2xl font-extrabold leading-tight">{next.title}</p>
          <p className="text-sm text-white/70 mt-1">{next.type} · {(next.date || "").slice(0, 10)}</p>
          <Button onClick={() => navigate("/operations")} className="mt-4 h-12 w-full bg-white text-[#1B7A3E] hover:bg-white/90 font-bold" data-testid="today-beginner-cta">
            {pick(language, "यह काम करें", "Do this task")} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </>
      ) : (
        <div className="flex items-center gap-3"><CheckCircle2 className="h-8 w-8 text-white/90" /><div>
          <p className="text-lg font-bold">{pick(language, "सब काम पूरे!", "All caught up!")}</p>
          <p className="text-sm text-white/70">{pick(language, "आज कोई ज़रूरी काम बाकी नहीं।", "No urgent tasks for today.")}</p>
        </div></div>
      )}
    </div>
  );
}
